
import { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function MortgageCalculatorPage() {
  const [loanAmount, setLoanAmount] = useState(300000);
  const [downPayment, setDownPayment] = useState(60000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [propertyTax, setPropertyTax] = useState(3600);
  const [homeInsurance, setHomeInsurance] = useState(1200);
  const [pmi, setPmi] = useState(250);
  const [hoaFees, setHoaFees] = useState(0);

  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  useEffect(() => {
    calculatePayment();
  }, [loanAmount, downPayment, interestRate, loanTerm, propertyTax, homeInsurance, pmi, hoaFees]);

  const calculatePayment = () => {
    const principal = loanAmount - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    if (monthlyRate === 0) {
      setMonthlyPayment(principal / numPayments);
    } else {
      const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                     (Math.pow(1 + monthlyRate, numPayments) - 1);
      setMonthlyPayment(payment);
    }

    const monthlyTax = propertyTax / 12;
    const monthlyInsurance = homeInsurance / 12;
    const monthlyPmi = downPayment < loanAmount * 0.2 ? pmi : 0;
    const monthlyHoa = hoaFees;

    setTotalMonthly(monthlyPayment + monthlyTax + monthlyInsurance + monthlyPmi + monthlyHoa);
    setTotalInterest((monthlyPayment * loanTerm * 12) - (loanAmount - downPayment));
  };

  const downPaymentPercent = ((downPayment / loanAmount) * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">
              Mortgage Calculator
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Calculate your monthly mortgage payment and see how different loan terms affect your budget. Get accurate estimates for principal, interest, taxes, and insurance.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <div className="bg-white rounded-lg p-8">
              <h2 className="text-2xl font-bold text-black mb-6">Loan Details</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Home Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(Number(e.target.value))}
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                      placeholder="300,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-2">
                    Down Payment
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(Number(e.target.value))}
                      className="w-full pl-8 pr-16 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                      placeholder="60,000"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      {downPaymentPercent}%
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Interest Rate
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        value={interestRate}
                        onChange={(e) => setInterestRate(Number(e.target.value))}
                        className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                        placeholder="6.5"
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Loan Term
                    </label>
                    <select
                      value={loanTerm}
                      onChange={(e) => setLoanTerm(Number(e.target.value))}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                    >
                      <option value={15}>15 years</option>
                      <option value={20}>20 years</option>
                      <option value={25}>25 years</option>
                      <option value={30}>30 years</option>
                    </select>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-black mt-8 mb-4">Additional Monthly Costs</h3>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Property Tax (Annual)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={propertyTax}
                        onChange={(e) => setPropertyTax(Number(e.target.value))}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                        placeholder="3,600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Home Insurance (Annual)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={homeInsurance}
                        onChange={(e) => setHomeInsurance(Number(e.target.value))}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                        placeholder="1,200"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      PMI (Monthly)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={pmi}
                        onChange={(e) => setPmi(Number(e.target.value))}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                        placeholder="250"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Required if down payment &lt; 20%
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      HOA Fees (Monthly)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={hoaFees}
                        onChange={(e) => setHoaFees(Number(e.target.value))}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-lg p-8">
              <h2 className="text-2xl font-bold text-black mb-6">Monthly Payment Breakdown</h2>
              
              <div className="space-y-6">
                <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 text-center">
                  <div className="text-3xl font-bold text-black mb-2">
                    ${totalMonthly.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                  <div className="text-gray-600">Total Monthly Payment</div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Principal & Interest</span>
                    <span className="font-semibold text-black">
                      ${monthlyPayment.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Property Tax</span>
                    <span className="font-semibold text-black">
                      ${(propertyTax / 12).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Home Insurance</span>
                    <span className="font-semibold text-black">
                      ${(homeInsurance / 12).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  
                  {downPayment < loanAmount * 0.2 && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-600">PMI</span>
                      <span className="font-semibold text-black">
                        ${pmi.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                  
                  {hoaFees > 0 && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-600">HOA Fees</span>
                      <span className="font-semibold text-black">
                        ${hoaFees.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mt-6">
                  <h3 className="font-bold text-black mb-3">Loan Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan Amount:</span>
                      <span className="text-black">${(loanAmount - downPayment).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Interest:</span>
                      <span className="text-black">${totalInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Paid:</span>
                      <span className="text-black">${(totalInterest + (loanAmount - downPayment)).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Loan Programs */}
          <div className="mt-12 bg-white rounded-lg p-8">
            <h2 className="text-3xl font-bold text-black mb-8 text-center">Popular Loan Programs</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Conventional Loan</h3>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment:</span>
                    <span className="text-black">3% - 20%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Rate:</span>
                    <span className="text-black">6.2% - 6.8%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Limit:</span>
                    <span className="text-black">$766,550</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">Most flexible loan option with competitive rates for qualified borrowers.</p>
              </div>

              <div className="border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">FHA Loan</h3>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment:</span>
                    <span className="text-black">3.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Rate:</span>
                    <span className="text-black">6.0% - 6.6%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Limit:</span>
                    <span className="text-black">$498,257</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">Government-backed loan with lower down payment and credit requirements.</p>
              </div>

              <div className="border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">VA Loan</h3>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Down Payment:</span>
                    <span className="text-black">0%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Rate:</span>
                    <span className="text-black">5.8% - 6.4%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Loan Limit:</span>
                    <span className="text-black">No limit</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">Exclusive benefit for veterans and active military with no down payment required.</p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-yellow-500 text-black rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Pre-Approved?</h3>
              <p className="text-black mb-6">
                Get connected with trusted lenders who can provide accurate rates and help you secure financing for your dream home.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-black text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black border-2 border-black transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-bank-line mr-2"></i>
                  Connect with Lenders
                </button>
                <button className="bg-transparent border-2 border-black text-black px-8 py-3 rounded-lg hover:bg-black hover:text-white transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}
